import { z } from "zod";

export const LEADING_SPACE_MESSAGE = "Cannot start with spaces";
export const DUPLICATE_SPACE_MESSAGE = "Remove duplicate spaces";
export const INVALID_TITLE_CHARACTERS_MESSAGE =
  "Only letters, numbers, spaces, and - ' & . are allowed";
export const INVALID_DESCRIPTION_CHARACTERS_MESSAGE =
  "Description contains invalid characters";
export const INVALID_PERSON_NAME_CHARACTERS_MESSAGE =
  "Only letters, spaces, and - ' are allowed";
export const INVALID_LABEL_CHARACTERS_MESSAGE =
  "Only letters, numbers, spaces, and - ' & . are allowed";
export const LABEL_REQUIRES_LETTER_MESSAGE =
  "Name must contain at least one letter";

/** Class / benefit titles — letters required; digits allowed (e.g. "Music 101"). */
export const TITLE_TEXT_PATTERN = /^(?=.*[A-Za-z])[A-Za-z0-9\s'&.-]+$/;
export const TITLE_TEXT_INPUT_FILTER = /[^A-Za-z0-9\s'&.-]/g;

/** Level / catalog labels — letters required; digits allowed (e.g. "10 years above"). */
export const LABEL_TEXT_PATTERN = /^(?=.*[A-Za-z])[A-Za-z0-9\s'&.-]+$/;
export const LABEL_TEXT_INPUT_FILTER = /[^A-Za-z0-9\s'&.-]/g;

/** Person names — letters required; hyphen and apostrophe only. */
export const PERSON_NAME_PATTERN = /^(?=.*[A-Za-z])[A-Za-z\s'-]+$/;
export const PERSON_NAME_INPUT_FILTER = /[^A-Za-z\s'-]/g;

/** Descriptions — letters required; allows digits and sentence punctuation. */
export const DESCRIPTION_TEXT_PATTERN =
  /^(?=.*[A-Za-z])[A-Za-z0-9\s.,'"\-:;!?()&]+$/;
export const DESCRIPTION_TEXT_INPUT_FILTER = /[^A-Za-z0-9\s.,'"\-:;!?()&]/g;

/** Trim edges and collapse internal runs of whitespace to a single space. */
export function normalizeTextInput(value: string): string {
  return value.trim().replace(/\s+/g, " ");
}

function hasLeadingWhitespace(value: string): boolean {
  return /^\s/.test(value);
}

function hasDuplicateWhitespace(value: string): boolean {
  return /\s{2,}/.test(value);
}

type TrimmedStringOptions = {
  requiredMessage: string;
  min?: number;
  minMessage?: string;
  max: number;
  maxMessage?: string;
  textPattern?: RegExp;
  invalidCharactersMessage?: string;
};

function buildTrimmedRequiredString(options: TrimmedStringOptions) {
  return z
    .string()
    .superRefine((value, ctx) => {
      if (hasLeadingWhitespace(value)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: LEADING_SPACE_MESSAGE,
        });
      }
      if (hasDuplicateWhitespace(value)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: DUPLICATE_SPACE_MESSAGE,
        });
      }
      if (
        options.textPattern &&
        value &&
        !options.textPattern.test(value)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            options.invalidCharactersMessage ??
            "Contains invalid characters",
        });
      }
    })
    .transform((value) => normalizeTextInput(value))
    .pipe(
      z
        .string()
        .min(options.min ?? 1, options.minMessage ?? options.requiredMessage)
        .max(
          options.max,
          options.maxMessage ?? `Max ${options.max} characters`
        )
    );
}

export function trimmedRequiredString(options: TrimmedStringOptions) {
  return buildTrimmedRequiredString(options);
}

export function trimmedRequiredTitleString(
  options: Omit<TrimmedStringOptions, "textPattern" | "invalidCharactersMessage">
) {
  return buildTrimmedRequiredString({
    ...options,
    textPattern: TITLE_TEXT_PATTERN,
    invalidCharactersMessage: INVALID_TITLE_CHARACTERS_MESSAGE,
  });
}

export function trimmedRequiredDescriptionString(
  options: Omit<TrimmedStringOptions, "textPattern" | "invalidCharactersMessage">
) {
  return buildTrimmedRequiredString({
    ...options,
    textPattern: DESCRIPTION_TEXT_PATTERN,
    invalidCharactersMessage: INVALID_DESCRIPTION_CHARACTERS_MESSAGE,
  });
}

export function trimmedRequiredPersonNameString(
  options: Omit<TrimmedStringOptions, "textPattern" | "invalidCharactersMessage">
) {
  return buildTrimmedRequiredString({
    ...options,
    textPattern: PERSON_NAME_PATTERN,
    invalidCharactersMessage: INVALID_PERSON_NAME_CHARACTERS_MESSAGE,
  });
}

export function sanitizeLabelTextInput(value: string): string {
  return value.replace(/^\s+/, "").replace(LABEL_TEXT_INPUT_FILTER, "");
}

export function getLabelTextValidationError(
  value: string,
  options?: { previousLabel?: string }
): string | null {
  const trimmed = normalizeTextInput(value);
  if (!trimmed) return "Required Fields Missing";

  const previous = options?.previousLabel
    ? normalizeTextInput(options.previousLabel)
    : undefined;
  if (previous && trimmed === previous) return null;

  if (!/[A-Za-z]/.test(trimmed)) return LABEL_REQUIRES_LETTER_MESSAGE;
  if (!LABEL_TEXT_PATTERN.test(trimmed)) {
    return INVALID_LABEL_CHARACTERS_MESSAGE;
  }
  return null;
}

/** Validates level label text; returns normalized value or throws with a UI-safe message. */
export function assertValidLabelText(
  value: string,
  options?: { previousLabel?: string }
): string {
  const error = getLabelTextValidationError(value, options);
  if (error) throw new Error(error);
  return normalizeTextInput(value);
}

export function trimmedOptionalString(options: {
  max: number;
  maxMessage?: string;
  textPattern?: RegExp;
  invalidCharactersMessage?: string;
}) {
  return z
    .string()
    .superRefine((value, ctx) => {
      if (!value) return;
      if (hasLeadingWhitespace(value)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: LEADING_SPACE_MESSAGE,
        });
      }
      if (hasDuplicateWhitespace(value)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: DUPLICATE_SPACE_MESSAGE,
        });
      }
      if (
        options.textPattern &&
        !options.textPattern.test(value)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            options.invalidCharactersMessage ??
            "Contains invalid characters",
        });
      }
    })
    .transform((value) => normalizeTextInput(value))
    .pipe(
      z
        .string()
        .max(
          options.max,
          options.maxMessage ?? `Max ${options.max} characters`
        )
    );
}
